import PlanningToolbar from './planning/PlanningToolbar';
import { SegmentEditor, JobEditor, GroupEditor, Onboarding } from './planning/Editors';
import { ZOOMS, STATUS, RT, snap, localValue, unixValue, timeLabel, menuPosition } from './planning/presentation';
import { readPreferences, writePreferences } from './planning/preferences';
import { assignLanes, planningLabels } from './planning/timeline';
import { confirmPendingChanges } from './hooks/useUnsavedChanges';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { showAppAlert, showAppConfirm } from './api';
import './JobPlanner.css';
import { StatTile } from './ui';
import { requestJson as request } from './api';
export { JobEditor, Onboarding } from './planning/Editors';
function ContextMenu({
  menu,
  onAction
}) {
  if (!menu) return null;
  return <div className="jp-context" style={{
    left: menu.x,
    top: menu.y
  }} onMouseDown={e => e.stopPropagation()} onContextMenu={e => e.preventDefault()}>
  {menu.kind === 'empty' && <button onClick={() => onAction('create')}>＋ Zuweisung erstellen</button>}
  {menu.kind === 'segment' && <><button onClick={() => onAction('edit')}>Bearbeiten</button><button onClick={() => onAction('duplicate')}>Duplizieren</button><button className="danger" onClick={() => onAction('deleteSegment')}>Zuweisung löschen</button></>}
  {menu.kind === 'job' && <><button onClick={() => onAction('editJob')}>Jobeinstellungen</button><button className="danger" onClick={() => onAction('deleteJob')}>Job löschen</button></>}
  {menu.kind === 'group' && <><button onClick={() => onAction('editGroup')}>Ladung bearbeiten</button><button className="danger" onClick={() => onAction('deleteGroup')}>Ladung löschen</button></>}
 </div>;
}
export default function JobPlanner() {
  const [mobile, setMobile] = useState(() => window.matchMedia?.('(max-width: 767px)').matches || false);
  useEffect(() => {
    const query = window.matchMedia?.('(max-width: 767px)');
    if (!query) return;
    const update = e => setMobile(e.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  const [view, setView] = useState(() => new URLSearchParams(window.location.search).get('job') ? 'jobs' : new URLSearchParams(window.location.search).get('view') === 'resources' ? 'resources' : readPreferences().view),
    [hours, setHours] = useState(() => {
      const h = Number(new URLSearchParams(window.location.search).get('hours'));
      return ZOOMS.includes(h) ? h : readPreferences().hours;
    }),
    [anchor, setAnchor] = useState(() => {
      const at = Number(new URLSearchParams(window.location.search).get('at'));
      if (at > 0) return at;
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return Math.floor(d / 1000);
    });
  const [data, setData] = useState({
      jobs: [],
      segments: [],
      groups: [],
      resources: [],
      drivers: [],
      vehicles: [],
      conflicts: [],
      gaps: [],
      migrationIssues: []
    }),
    [editor, setEditor] = useState(null),
    [jobEditor, setJobEditor] = useState(null),
    [groupEditor, setGroupEditor] = useState(null),
    [onboarding, setOnboarding] = useState(false),
    [undo, setUndo] = useState(null),
    [collapsed, setCollapsed] = useState({}),
    [loading, setLoading] = useState(true),
    [menu, setMenu] = useState(null),
    [panning, setPanning] = useState(false),
    [segmentPreview, setSegmentPreview] = useState(null),
    [segmentDragging, setSegmentDragging] = useState(false),
    [dragFeedback, setDragFeedback] = useState(null),
    [dragSaving, setDragSaving] = useState(false),
    [archiveFilter, setArchiveFilter] = useState(() => new URLSearchParams(window.location.search).has('job') ? 'all' : 'active'),
    [zoom, setZoom] = useState(1),
    [timelineWidth, setTimelineWidth] = useState(1000),
    [mobileLimit, setMobileLimit] = useState(30);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('q') || ''),
    [statusFilter, setStatusFilter] = useState(() => new URLSearchParams(window.location.search).get('status') || ''),
    [summary, setSummary] = useState(null);
  useEffect(() => {
    request('/planning/summary').then(setSummary).catch(() => {});
  }, [data]);
  useEffect(() => {
    writePreferences(view, hours);
  }, [view, hours]);
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;
    setScrollTop(0);
  }, [search, statusFilter]);
  const [scrollTop, setScrollTop] = useState(0);
  const pendingFocus = useRef(new URLSearchParams(window.location.search).get('job') ? 'j' + new URLSearchParams(window.location.search).get('job') : null);
  const [resourceFilter, setResourceFilter] = useState(() => { const value=new URLSearchParams(window.location.search).get('resource') || ''; return value==='AREA'?'':value; }),
    [problemsOpen, setProblemsOpen] = useState(false),
    [loadError, setLoadError] = useState('');
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = 0; setScrollTop(0); }, [archiveFilter, view, resourceFilter]);
  useEffect(() => {
    const url = new URL(window.location.href);
    if (search) url.searchParams.set('q', search);else url.searchParams.delete('q');
    if (statusFilter) url.searchParams.set('status', statusFilter);else url.searchParams.delete('status');
    window.history.replaceState(null, '', url);
  }, [search, statusFilter]);
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('view', view);
    url.searchParams.set('hours', hours);
    url.searchParams.set('at', anchor);
    if (resourceFilter) url.searchParams.set('resource', resourceFilter);else url.searchParams.delete('resource');
    window.history.replaceState(null, '', url);
  }, [view, hours, anchor, resourceFilter]);
  const problemIndex = useRef(-1);
  const [clock, setClock] = useState(Date.now() / 1000);
  useEffect(() => {
    const id = setInterval(() => setClock(Date.now() / 1000), 30000);
    return () => clearInterval(id);
  }, []);
  const scroller = useRef(null),
    pan = useRef(null),
    segmentDrag = useRef(null),
    savingDrag = useRef(false);
  const {from,to} = useMemo(() => {
    if (hours < 24) return {from: anchor, to: anchor + hours * 3600};
    const start = new Date(anchor * 1000); start.setHours(0,0,0,0);
    const end = new Date(start); end.setDate(end.getDate() + hours / 24);
    return {from: start.getTime()/1000, to: end.getTime()/1000};
  }, [anchor,hours]);
  const loadSequence = useRef(0);
  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    try {
      const result = await request('/planning/timeline');
      if (sequence === loadSequence.current) {
        setData(result);
        setLoadError('');
      }
      return result;
    } catch (err) {
      if (sequence === loadSequence.current) setLoadError(err.message);
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(()=>{
    const restore=()=>{const params=new URLSearchParams(window.location.search);if(!['#planner','#loadingBayPlanner'].includes(window.location.hash))return;
      const job=params.get('job'),at=Number(params.get('at')),zoom=Number(params.get('hours'));
      setSearch(params.get('q')||'');setStatusFilter(params.get('status')||'');setResourceFilter(params.get('resource')==='AREA'?'':params.get('resource')||'');
      setView(job?'jobs':params.get('view')==='resources'?'resources':'jobs');if(ZOOMS.includes(zoom))setHours(zoom);if(at>0)setAnchor(at);
      if(job){setArchiveFilter('all');pendingFocus.current='j'+job;setCollapsed(old=>({...old,[job]:false}));}
    };
    window.addEventListener('dispoboard:planning-focus',restore);
    return()=>window.removeEventListener('dispoboard:planning-focus',restore);
  },[]);
  const closeMenu = useCallback(() => setMenu(null), []);
  useEffect(() => {
    const preview = (d, start, end) => {
      const key = start + ':' + end;
      if (d.previewKey === key) return d.previewPromise;
      d.previewKey = key;
      d.previewPromise = request('/planning/segments/preview', {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({...d.segment, start_time: start, end_time: end})
      }).then(result => {
        if (!Array.isArray(result.problems)) throw new Error('Ungültige Antwort der Konfliktprüfung');
        return result.problems.filter(p => p.hard && p.segmentIds?.some(id => Number(id) === Number(d.segment.id)));
      });
      return d.previewPromise;
    };
    const schedulePreview = d => {
      clearTimeout(d.timer);
      const start = d.nextStart, end = d.nextEnd;
      setDragFeedback({kind: 'checking', text: 'Konflikte werden geprüft …'});
      d.timer = setTimeout(() => {
        preview(d, start, end).then(problems => {
          if (segmentDrag.current !== d || d.nextStart !== start || d.nextEnd !== end) return;
          setDragFeedback(problems.length ? {kind: 'conflict', text: problems.map(p => p.message).filter(Boolean).join(' · ') || 'Konflikt im Zeitraum'} : {kind: 'clear', text: 'Keine Konflikte erkannt'});
        }).catch(() => {
          if (segmentDrag.current === d && d.nextStart === start && d.nextEnd === end) setDragFeedback({kind: 'error', text: 'Konfliktprüfung nicht verfügbar'});
        });
      }, 250);
    };
    const move = e => {
      const d = segmentDrag.current;
      if (d) {
        if ((e.type.startsWith('touch')) !== (d.input === 'touch')) return;
        const point = d.input === 'touch' ? Array.from(e.touches).find(t => t.identifier === d.touchId) : e;
        if (!point) return;
        const pixels = point.clientX - d.x;
        if (!d.active && Math.abs(pixels) < 4) return;
        d.active = true;
        const delta = snap(pixels / d.width * (to - from), hours <= 12 ? 60 : 900);
        let start = d.start,
          end = d.end;
        if (d.mode === 'move') {
          start = d.start + delta;
          end = d.end + delta;
        } else if (d.mode === 'start') start = Math.min(d.end - (hours <= 12 ? 60 : 900), d.start + delta);else end = Math.max(d.start + (hours <= 12 ? 60 : 900), d.end + delta);
        if (d.nextStart !== start || d.nextEnd !== end) {
          d.nextStart = start;
          d.nextEnd = end;
          schedulePreview(d);
        }
        setSegmentPreview({
          id: d.segment.id,
          start_time: start,
          end_time: end
        });
        setSegmentDragging(true);
        e.preventDefault();
        return;
      }
      if (e.type.startsWith('touch')) return;
      const p = pan.current;
      if (!p) return;
      const dx = e.clientX - p.x,
        dy = e.clientY - p.y;
      if (!p.active && Math.hypot(dx, dy) < 5) return;
      p.active = true;
      setPanning(true);
      scroller.current.scrollLeft = p.left - dx;
      scroller.current.scrollTop = p.top - dy;
      e.preventDefault();
    };
    const up = async e => {
      const d = segmentDrag.current;
      if (d && (e.type.startsWith('touch')) !== (d.input === 'touch')) return;
      if (d?.input === 'touch' && !Array.from(e.changedTouches).some(t => t.identifier === d.touchId)) return;
      segmentDrag.current = null;
      setSegmentDragging(false);
      if (d) {
        clearTimeout(d.timer);
        if (!d.active) {
          setSegmentPreview(null);
          setDragFeedback(null);
          editSegment(d.segment);
          return;
        }
        if (d.nextStart === d.start && d.nextEnd === d.end) { setSegmentPreview(null); setDragFeedback(null); return; }
        savingDrag.current = true;
        setDragSaving(true);
        setDragFeedback({kind: 'checking', text: 'Zeitraum wird geprüft …'});
        try {
          let warnings;
          try { warnings = await preview(d, d.nextStart, d.nextEnd); }
          catch (err) {
            const proceed = await showAppConfirm('Die Konfliktprüfung ist fehlgeschlagen: ' + err.message + '. Zeitraum trotzdem speichern?', {danger: true});
            if (!proceed) return;
          }
          if (warnings?.length && !(await showAppConfirm('Konflikt erkannt: ' + [...new Set(warnings.map(p => p.message))].join(' · ') + '. Zeitraum trotzdem speichern?', {danger: true}))) return;
          const out = await request('/planning/segments/' + d.segment.id, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...d.segment,
              start_time: d.nextStart,
              end_time: d.nextEnd,
              version: d.segment.version
            })
          });
          if (out.mutationId) setUndo(out.mutationId);
          await load();
        } catch (err) {
          showAppAlert(err.message, {
            title: 'Zeitraum nicht gespeichert',
            danger: true
          });
          await load();
        } finally {
          savingDrag.current = false;
          setDragSaving(false);
          setSegmentPreview(null);
          setDragFeedback(null);
        }
        return;
      }
      pan.current = null;
      setPanning(false);
    };
    const cancel = () => {
        if (savingDrag.current) return;
        if (segmentDrag.current) clearTimeout(segmentDrag.current.timer);
        segmentDrag.current = null;
        pan.current = null;
        setSegmentPreview(null);
        setSegmentDragging(false);
        setDragFeedback(null);
        setPanning(false);
        closeMenu();
    };
    const key = e => { if (e.key === 'Escape' && !savingDrag.current) cancel(); };
    window.addEventListener('mousemove', move, {
      passive: false
    });
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, {passive: false});
    window.addEventListener('touchend', up);
    window.addEventListener('touchcancel', cancel);
    window.addEventListener('blur', cancel);
    window.addEventListener('keydown', key);
    window.addEventListener('resize', closeMenu);
    return () => {
      if (segmentDrag.current) clearTimeout(segmentDrag.current.timer);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
      window.removeEventListener('touchcancel', cancel);
      window.removeEventListener('blur', cancel);
      window.removeEventListener('keydown', key);
      window.removeEventListener('resize', closeMenu);
    };
  }, [closeMenu, from, to, hours, load]);
  const visibleJobIds = useMemo(() => new Set(data.jobs.filter(j => archiveFilter === 'all' || (archiveFilter === 'archived' ? j.archived : !j.archived)).map(j => Number(j.id))), [data.jobs,archiveFilter]);
  const rows = useMemo(() => view === 'resources' ? [...data.resources.filter(r => r.type !== 'AREA'), ...data.drivers.map(d => ({
    ...d,
    type: 'DRIVER'
  })), ...data.vehicles.map(v => ({
    ...v,
    name: v.license_plate,
    type: 'VEHICLE'
  }))].filter(r => (!resourceFilter || r.type === resourceFilter) && (!search || String(r.name).toLocaleLowerCase().includes(search.toLocaleLowerCase()))).map(r => ({
    key: 'r' + r.type + r.id,
    title: r.name,
    meta: RT[r.type],
    resource: r
  })) : data.jobs.filter(job => visibleJobIds.has(Number(job.id)) && (!search || [job.display_name, job.name, job.customer].some(v => String(v || '').toLocaleLowerCase().includes(search.toLocaleLowerCase())))).flatMap(job => {
    const groups = data.groups.filter(g => Number(g.job_id) === Number(job.id)),
      parent = {
        key: 'j' + job.id,
        title: job.display_name || job.name,
        meta: job.customer || 'Ohne Kunde',
        job,
        parent: groups.length > 0
      };
    return collapsed[job.id] || !groups.length ? [parent] : [parent, ...groups.map(group => ({
      key: 'g' + group.id,
      title: group.name,
      meta: job.display_name || job.name,
      job,
      group
    }))];
  }), [view, data, collapsed, search, resourceFilter, visibleJobIds]);
  const segmentsByRow = useMemo(() => {
    const index = new Map();
    const add = (key, segment) => {
      if (!index.has(key)) index.set(key, []);
      index.get(key).push(segment);
    };
    for (const segment of data.segments) {
      if (!visibleJobIds.has(Number(segment.job_id))) continue;
      if (Number(segment.start_time) >= to || Number(segment.end_time) <= from || (statusFilter && segment.status !== statusFilter)) continue;
      add('j' + segment.job_id, segment);
      if (segment.load_group_id != null) add('g' + segment.load_group_id, segment);
      if (segment.resource_type && segment.resource_id != null) add('r' + segment.resource_type + segment.resource_id, segment);
      if (segment.driver_id != null) add('rDRIVER' + segment.driver_id, segment);
      if (segment.vehicle_id != null) add('rVEHICLE' + segment.vehicle_id, segment);
    }
    return index;
  }, [data.segments, statusFilter, from, to, visibleJobIds]);
  const rowSegments = useCallback(row => {
    const segments = segmentsByRow.get(row.key) || [];
    return row.parent ? segments.filter(segment => !segment.load_group_id) : segments;
  }, [segmentsByRow]);
  useEffect(() => setMobileLimit(30), [view, search, statusFilter, resourceFilter]);
  const rowLayouts = useMemo(() => {
    let offset = 0;
    return rows.map(row => {
      const segments = rowSegments(row),
        layout = assignLanes(segments),
        height = Math.max(row.group ? 66 : 78, layout.count * 64 + 16);
      const item = {
        row,
        segments,
        layout,
        height,
        offset
      };
      offset += height;
      return item;
    });
  }, [rows, rowSegments]);
  const totalHeight = rowLayouts.length ? rowLayouts[rowLayouts.length - 1].offset + rowLayouts[rowLayouts.length - 1].height : 0;
  const visibleRows = rowLayouts.filter(item => item.offset + item.height >= Math.max(0, scrollTop - 250) && item.offset <= scrollTop + (scroller.current?.clientHeight || 600) + 250);
  useEffect(() => {
    if (!pendingFocus.current) return;
    const item = rowLayouts.find(x => x.row.key === pendingFocus.current);
    if (item && scroller.current) {
      scroller.current.scrollTop = item.offset;
      setScrollTop(item.offset);
      pendingFocus.current = null;
    }
  }, [rowLayouts]);
  useEffect(() => {
    const el = scroller.current; if (!el) return;
    const measure = () => setTimelineWidth(Math.max(100, el.clientWidth - (el.querySelector('.jp-label-head')?.offsetWidth || 280)));
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(el); window.addEventListener('resize',measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize',measure); };
  }, []);
  useEffect(() => { if (scroller.current) scroller.current.scrollLeft = 0; }, [from,to,hours,zoom]);
  const handleTimelineScroll = e => {
    const el = e.currentTarget;
    closeMenu();
    setScrollTop(el.scrollTop);
  };
  const x = t => (Number(t) - from) / (to - from) * 100,
    ticks = useMemo(() => {
      const target = Math.max(1, Math.floor(timelineWidth * zoom / 90));
      const stepHours = [.25,.5,1,2,3,6,12,24,48,72].find(step => hours / step <= target) || 72;
      const tick = new Date(from * 1000);
      if (stepHours < 1) tick.setMinutes(Math.floor(tick.getMinutes() / (stepHours*60)) * stepHours*60, 0, 0);
      else { tick.setMinutes(0, 0, 0); tick.setHours(stepHours >= 24 ? 0 : Math.floor(tick.getHours() / stepHours) * stepHours); }
      const out = [];
      while (tick.getTime() / 1000 <= to) {
        const time = tick.getTime() / 1000;
        if (time >= from && time < to) out.push(time);
        if (stepHours <= 1) tick.setTime(tick.getTime() + stepHours*3600000);
        else if (stepHours >= 24) tick.setDate(tick.getDate() + stepHours / 24);
        else tick.setHours(tick.getHours() + stepHours);
      }
      return out;
    }, [from, to, hours, timelineWidth, zoom]);
  const editSegment = async s => {
    if (!(await confirmPendingChanges())) return;
    setJobEditor(null);
    setGroupEditor(null);
    setEditor({
      ...s,
      start_time: localValue(s.start_time),
      end_time: localValue(s.end_time)
    });
  };
  const beginSegmentDrag = (e, s, mode = 'move') => {
    if (savingDrag.current || segmentDrag.current || (e.type !== 'touchstart' && e.button !== 0)) return;
    const touch = e.type === 'touchstart' ? e.touches[0] : null;
    if (e.type === 'touchstart' && (!touch || e.touches.length !== 1)) return;
    e.preventDefault();
    e.stopPropagation();
    closeMenu();
    const rect = e.currentTarget.closest('.jp-track').getBoundingClientRect();
    if (!rect.width) return;
    segmentDrag.current = {
      segment: s,
      mode,
      input: touch ? 'touch' : 'mouse',
      touchId: touch?.identifier,
      x: touch ? touch.clientX : e.clientX,
      width: rect.width,
      start: Number(s.start_time),
      end: Number(s.end_time),
      active: false,
      nextStart: Number(s.start_time),
      nextEnd: Number(s.end_time)
    };
  };
  const saved = async id => {
    setOnboarding(false);
    if (id) setUndo(id);
    const fresh = await load();
    if (!fresh) { setEditor(null); showAppAlert('Die Änderung wurde gespeichert, aber die Planung konnte nicht aktualisiert werden. Bitte erneut laden.', {title:'Aktualisierung fehlgeschlagen'}); return; }
    if (editor?.id) setEditor(fresh.segments.find(s => s.id === editor.id) ? {
      ...fresh.segments.find(s => s.id === editor.id),
      start_time: localValue(fresh.segments.find(s => s.id === editor.id).start_time),
      end_time: localValue(fresh.segments.find(s => s.id === editor.id).end_time)
    } : null);else setEditor(null);
    if (jobEditor) setJobEditor(fresh.jobs.find(j => j.id === jobEditor.id) || null);
    if (groupEditor) setGroupEditor(fresh.groups.find(g => g.id === groupEditor.id) || null);
  };
  const created = async result => {
    setOnboarding(false);
    const fresh = await load();
    const id = result?.id || result?.job?.id || result?.jobId;
    const job = fresh?.jobs.find(j => Number(j.id) === Number(id));
    if (job) {
      setView('jobs');setSearch('');setStatusFilter('');setCollapsed(old=>({...old,[job.id]:false}));
      setJobEditor(job);
      setAnchor(Number(job.dispo_start) || anchor);
      pendingFocus.current = 'j' + job.id;
    }
  };
  const createAssignment = async (row, time = anchor) => {
    if (!(await confirmPendingChanges())) return;
    closeMenu();
    setJobEditor(null);
    setGroupEditor(null);
    setEditor({
      job_id: row?.job?.id || '',
      load_group_id: row?.group?.id || null,
      status: 'PACKED',
      start_time: localValue(time),
      end_time: localValue(time + 900),
      resource_type: ['DRIVER', 'VEHICLE'].includes(row?.resource?.type) ? null : row?.resource?.type || null,
      resource_id: ['DRIVER', 'VEHICLE'].includes(row?.resource?.type) ? null : row?.resource?.id ?? null,
      driver_id: row?.resource?.type === 'DRIVER' ? row.resource.id : null,
      vehicle_id: row?.resource?.type === 'VEHICLE' ? row.resource.id : null,
      notes: ''
    });
  };
  const focusProblem = async p => {
    if (!(await confirmPendingChanges())) return;
    const jobId = p.job_id || p.jobIds?.[0];
    if (jobId) {
      setCollapsed(old => ({
        ...old,
        [jobId]: false
      }));
      pendingFocus.current = p.load_group_id ? 'g' + p.load_group_id : 'j' + jobId;
      setView('jobs');
      setSearch('');
    }
    if (p.start_time) setAnchor(Number(p.start_time));
  };
  const beginPan = e => {
    if (e.button !== 0 || e.target !== e.currentTarget) return;
    closeMenu();
    pan.current = {
      x: e.clientX,
      y: e.clientY,
      left: scroller.current.scrollLeft,
      top: scroller.current.scrollTop,
      active: false
    };
  };
  const openEmptyMenu = (e, row) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect(),
      time = snap(from + (e.clientX - rect.left) / rect.width * (to - from), hours <= 12 ? 60 : 900),
      pos = menuPosition(e.clientX, e.clientY, 1);
    setMenu({
      ...pos,
      kind: 'empty',
      row,
      time
    });
  };
  const deleteSegment = async s => {
    if (!(await showAppConfirm('Zuweisung wirklich löschen?', {
      danger: true
    }))) return;
    try {
      const out = await request('/planning/segments/' + s.id, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          version: s.version
        })
      });
      setUndo(out.mutationId);
      closeMenu();
      load();
    } catch (err) {
      showAppAlert(err.message);
    }
  };
  const deleteJob = async job => {
    if (!(await showAppConfirm('Job „' + (job.display_name || job.name) + '“ einschließlich aller Ladungen und Zuweisungen endgültig löschen?', {
      title: 'Job löschen',
      danger: true
    }))) return;
    try {
      await request('/planning/jobs/' + job.id, {
        method: 'DELETE'
      });
      setJobEditor(null);
      closeMenu();
      load();
    } catch (err) {
      showAppAlert(err.message, {
        danger: true
      });
    }
  };
  const deleteGroup = async group => {
    if (!(await showAppConfirm('Ladung „' + group.name + '“ einschließlich ihrer Zuweisungen endgültig löschen?', {
      title: 'Ladung löschen',
      danger: true
    }))) return;
    try {
      await request('/planning/groups/' + group.id, {
        method: 'DELETE'
      });
      setGroupEditor(null);
      closeMenu();
      load();
    } catch (err) {
      showAppAlert(err.message, {
        danger: true
      });
    }
  };
  const deleteAllLegacy = async () => {
    if (!(await showAppConfirm('Alle offenen Alt-Zuordnungen aus der Problemliste entfernen? Die historischen Slots bleiben zur Diagnose erhalten.', {
      title: 'Alle Alt-Zuordnungen l\u00f6schen',
      danger: true
    }))) return;
    try {
      await request('/planning/migration/issues', {
        method: 'DELETE'
      });
      load();
    } catch (err) {
      showAppAlert(err.message, {
        danger: true
      });
    }
  };
  const menuAction = async action => {
    if (action === 'create') { const current=menu; return createAssignment(current.row,current.time); }
    if (!(await confirmPendingChanges())) return;
    const current = menu;
    closeMenu();
    if (action === 'edit') editSegment(current.segment);
    if (action === 'duplicate') {
      const s = current.segment,
        duration = Number(s.end_time) - Number(s.start_time);
      setEditor({
        ...s,
        id: undefined,
        version: undefined,
        start_time: localValue(Number(s.end_time)),
        end_time: localValue(Number(s.end_time) + duration)
      });
    }
    if (action === 'deleteSegment') deleteSegment(current.segment);
    if (action === 'editJob') setJobEditor(current.row.job);
    if (action === 'deleteJob') deleteJob(current.row.job);
    if (action === 'editGroup') setGroupEditor(current.row.group);
    if (action === 'deleteGroup') deleteGroup(current.row.group);
  };
  const visibleConflicts = data.conflicts.filter(problem => Number(problem.start_time) < to && Number(problem.end_time) > from && (!problem.jobIds?.length || problem.jobIds.some(id => visibleJobIds.has(Number(id))))),
    visibleGaps = data.gaps.filter(problem => Number(problem.start_time) < to && Number(problem.end_time) > from && visibleJobIds.has(Number(problem.job_id)));
  const problems = [...visibleConflicts, ...visibleGaps, ...data.migrationIssues],
    nextProblem = () => {problemIndex.current=(problemIndex.current+1)%Math.max(1,problems.length);const problem=problems[problemIndex.current];if(problem)focusProblem(problem);};
  const changePeriod = next => {
    if(next===hours)return;
    const el=scroller.current, trackWidth=Math.max(1,(el?.clientWidth||0)-280), scroll=el?.scrollLeft||0;
    const center=el?.clientWidth ? from+(to-from)*Math.min(1,(scroll+trackWidth/2)/(trackWidth*zoom)) : from+(to-from)/2;
    const selected=editor?.id ? (unixValue(editor.start_time)+unixValue(editor.end_time))/2 : NaN;
    const focus=Number.isFinite(selected)&&selected>=from&&selected<to?selected:center;
    if(next<24)setAnchor(snap(focus-next*1800,60));
    else {const day=new Date(focus*1000);day.setHours(0,0,0,0);day.setDate(day.getDate()-Math.floor((next/24-1)/2));setAnchor(Math.floor(day.getTime()/1000));}
    setHours(next);setZoom(1);
  };

  return <div className={'jp-shell ' + (panning ? 'is-panning ' : '') + (segmentDragging ? 'is-segment-dragging' : '')} onMouseDown={e => {
    if (!e.target.closest('.jp-context')) closeMenu();
  }}><header className="jp-header"><div><h1>Planung</h1><p>Jobs, Ladungen und Ressourcen disponieren.</p></div><div><button onClick={async () => {
          if (await confirmPendingChanges()) setOnboarding(true);
        }}>＋ Job anlegen</button><button onClick={async () => {
          if (await confirmPendingChanges()) setOnboarding('EVENTWORX');
        }}>Importieren</button><button onClick={() => createAssignment()}>Zuweisung erstellen</button></div></header>
 <details className="planning-metrics" key={mobile ? "mobile" : "desktop"} open={!mobile}><summary>Kennzahlen</summary><div className="ui-stats"><StatTile label="Jobs heute" value={summary?.jobsToday ?? "–"} /><StatTile label="Konflikte im geladenen Zeitraum" value={loading || loadError ? "–" : visibleConflicts.length} /><StatTile label="Lücken im geladenen Zeitraum" value={loading || loadError ? "–" : visibleGaps.length} /><StatTile label="Ungeplante Jobs insgesamt" value={loading || loadError ? '–' : data.jobs.filter(j => j.planning_quality === 'UNPLANNED').length} /><StatTile label="Ressourcen aktuell verfügbar" value={summary ? summary.available + " / " + summary.resources : "–"} /></div></details>
 {loadError && <div role="alert" className="ui-surface">{loadError}<button onClick={load}>Erneut laden</button></div>}<PlanningToolbar {...{view, setView, anchor, setAnchor, hours, search, setSearch, statusFilter, setStatusFilter, resourceFilter, setResourceFilter, archiveFilter, setArchiveFilter, zoom, setZoom}} setHours={changePeriod} />
 {dragFeedback && <div role="status" aria-live="polite" className={'planning-drag-feedback ' + dragFeedback.kind}><strong>{dragSaving ? 'Änderung prüfen' : 'Zeitraum verschieben'}</strong><span>{dragFeedback.text}</span><small>{segmentPreview && `${timeLabel(segmentPreview.start_time, true)} – ${timeLabel(segmentPreview.end_time, true)}`}{!dragSaving && ' · Esc zum Abbrechen'}</small></div>}
 {problems.length > 0 && <section className="jp-problems"><button aria-expanded={problemsOpen} onClick={() => setProblemsOpen(x => !x)}>Planungsprobleme {problemsOpen ? '▾' : '▸'}</button><span>{visibleConflicts.length} Konflikte</span><span>{visibleGaps.length} Lücken</span><span>{data.migrationIssues.length} Alt-Zuordnungen</span>{data.migrationIssues.length > 0 && <button className="danger" onClick={deleteAllLegacy}>Alle l&ouml;schen</button>}<button onClick={nextProblem}>Nächstes Problem</button></section>}
 {problemsOpen && <div className="jp-problem-list">{problems.map((p, i) => <button key={i} onClick={() => focusProblem(p)}>{p.message || 'Planungslücke / Alt-Zuordnung'}{p.start_time ? ' ? ' + timeLabel(p.start_time, true) : ''}</button>)}</div>}
 {!loading && !loadError && rows.length === 0 && <div className="ui-empty" role="status">{search || resourceFilter || (archiveFilter !== 'all' && data.jobs.length) ? 'Keine passenden Einträge. Passen Sie die Filter an.' : view === 'jobs' ? 'Noch keine Jobs vorhanden. Legen Sie einen Job an oder importieren Sie ihn.' : 'Keine Ressourcen vorhanden.'}</div>}
 {mobile && <div className="planning-mobile" aria-label="Planung als Liste">
   <div className="planning-mobile-heading"><strong>{view === 'jobs' ? 'Jobs und Ladungen' : 'Ressourcen'}</strong><small>{timeLabel(from, true)} – {timeLabel(to, true)}</small></div>
   {rowLayouts.slice(0, mobileLimit).map(({row, segments}) => <article className={'planning-mobile-card' + (row.group ? ' is-child' : '')} key={row.key}>
     <div className="planning-mobile-card-head"><div><strong>{row.title}</strong><small>{row.meta}{row.job?.planning_quality ? ' · ' + (planningLabels[row.job.planning_quality] || row.job.planning_quality) : ''}</small></div>
       <div className="d-flex gap-1">{row.parent && <button type="button" className="btn" aria-label={(collapsed[row.job.id] ? 'Ladungen anzeigen: ' : 'Ladungen ausblenden: ') + row.title} onClick={() => setCollapsed(o => ({...o, [row.job.id]: !o[row.job.id]}))}>{collapsed[row.job.id] ? '▸' : '▾'}</button>}{row.job && <button type="button" className="btn" onClick={async () => {if (await confirmPendingChanges()) {if (row.group) setGroupEditor(row.group); else setJobEditor(row.job);}}}>Details</button>}<button type="button" className="btn" aria-label={'Zuweisung hinzufügen: ' + row.title} onClick={() => createAssignment(row)}>＋</button></div>
     </div>
     {segments.length ? <div className="planning-mobile-assignments">{segments.map(s => <button type="button" className="planning-mobile-assignment" key={s.id} onClick={() => editSegment(s)}><span className="planning-mobile-status" style={{background: STATUS[s.status]?.[1]}} /><span><strong>{STATUS[s.status]?.[0] || s.status}</strong><small>{timeLabel(s.start_time, true)} – {timeLabel(s.end_time, true)}</small></span><span aria-hidden="true">›</span></button>)}</div> : <p className="planning-mobile-empty">Keine Zuweisung im geladenen Zeitraum</p>}
   </article>)}
   {rowLayouts.length > mobileLimit && <button type="button" className="btn w-100" onClick={() => setMobileLimit(n => n + 30)}>Weitere {Math.min(30, rowLayouts.length - mobileLimit)} anzeigen</button>}
 </div>}
 <div className="jp-scroll" ref={scroller} onScroll={handleTimelineScroll}><div className="jp-board" style={{
        width: zoom === 1 ? '100%' : `calc(${zoom * 100}% - ${280 * (zoom - 1)}px)`
      }}><div className="jp-time-head"><div className="jp-label-head">{view === 'jobs' ? 'Job / Ladung' : 'Ressource'}</div><div className="jp-ticks">{ticks.map(t => <span style={{
              left: x(t) + '%', transform: x(t) < 2 ? 'none' : undefined
            }} key={t}>{timeLabel(t, hours > 24)}</span>)}</div></div><div className="jp-body">{loading && !data.jobs.length && <div className="jp-loading">Planung wird geladen…</div>}{visibleRows.length > 0 && <div style={{
            height: visibleRows[0].offset
          }} />}{visibleRows.map(({
            row,
            segments,
            layout,
            height
          }) => <div className={'jp-row ' + (row.group ? 'child' : '')} key={row.key} style={{
            height
          }}>
  <div className="jp-row-label" onContextMenu={e => {
              if (!row.job) return;
              e.preventDefault();
              const pos = menuPosition(e.clientX, e.clientY, 2);
              setMenu({
                ...pos,
                kind: row.group ? 'group' : 'job',
                row
              });
            }}>{row.parent && <button className="jp-toggle" onClick={() => setCollapsed(o => ({
                ...o,
                [row.job.id]: !o[row.job.id]
              }))}>{collapsed[row.job.id] ? '▸' : '▾'}</button>}<button className="jp-name" onClick={async () => {
                if (!(await confirmPendingChanges())) return;
                setEditor(null);
                setJobEditor(null);
                setGroupEditor(null);
                if (row.group) setGroupEditor(row.group);else if (row.job) setJobEditor(row.job);
              }}><strong>{row.group ? '↳ ' : ''}{row.title}</strong><small>{row.meta}</small>{row.job?.planning_quality && <em>{planningLabels[row.job.planning_quality] || row.job.planning_quality}</em>}</button><button className="jp-row-add" aria-label={"Zuweisung für " + row.title} title="Zuweisung erstellen" onClick={() => createAssignment(row)}>+</button></div>
  <div className="jp-track" onMouseDown={beginPan} onContextMenu={e => openEmptyMenu(e, row)}>{row.parent && <button className="jp-parent-period" style={{
                left: Math.max(0, x(row.job.dispo_start)) + '%',
                width: Math.max(0, Math.min(100, x(row.job.dispo_end)) - Math.max(0, x(row.job.dispo_start))) + '%'
              }} onMouseDown={e => e.stopPropagation()} onClick={async () => {
                if (await confirmPendingChanges()) {
                  setEditor(null);
                  setJobEditor(row.job);
                }
              }}>{row.title}</button>}{clock >= from && clock < to && <i className="jp-now" style={{
                left: x(clock) + '%'
              }} />}{ticks.map(t => <i className="jp-gridline" style={{
                left: x(t) + '%'
              }} key={t} />)}
   {visibleConflicts.filter(c => segments.some(s => c.segmentIds?.includes(Number(s.id)))).map((c, i) => <i className="jp-conflict" title={c.message} style={{
                left: Math.max(0, x(c.start_time)) + '%',
                width: Math.max(0, Math.min(100, x(c.end_time)) - Math.max(0, x(c.start_time))) + '%'
              }} key={'c' + i} />)}
   {view === 'jobs' && visibleGaps.filter(g => Number(g.job_id) === Number(row.job?.id) && (!row.group || Number(g.load_group_id) === Number(row.group.id))).map((g, i) => <i className="jp-gap" title="Planungslücke" style={{
                left: Math.max(0, x(g.start_time)) + '%',
                width: Math.max(0, Math.min(100, x(g.end_time)) - Math.max(0, x(g.start_time))) + '%'
              }} key={'gap' + i} />)}
   {segments.map(s => {
                const shown = segmentPreview?.id === s.id ? {
                  ...s,
                  ...segmentPreview
                } : s;
                return <button onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    editSegment(s);
                  }
                }} aria-label={`${row.title}: ${STATUS[s.status]?.[0]}, ${timeLabel(shown.start_time)} bis ${timeLabel(shown.end_time)}`} title={`${row.title} · ${STATUS[s.status]?.[0]} · ${timeLabel(shown.start_time)}–${timeLabel(shown.end_time)}`} data-status={s.status} className={'jp-segment' + (segmentPreview?.id === s.id ? ' is-preview' : '') + (segmentPreview?.id === s.id && dragFeedback?.kind === 'conflict' ? ' has-conflict' : '')} style={{
                  left: Math.max(0, x(shown.start_time)) + '%',
                  width: Math.max(.4, Math.min(100, x(shown.end_time)) - Math.max(0, x(shown.start_time))) + '%',
                  background: STATUS[s.status]?.[1],
                  top: 8 + (layout.lanes.get(s.id) || 0) * 64,
                  bottom: 'auto',
                  height: 56
                }} onClick={e => {
                  if (e.detail === 0) editSegment(s);
                }} onMouseDown={e => beginSegmentDrag(e, s, 'move')} onTouchStart={e => beginSegmentDrag(e, s, 'move')} onContextMenu={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  const pos = menuPosition(e.clientX, e.clientY, 3);
                  setMenu({
                    ...pos,
                    kind: 'segment',
                    segment: s,
                    row
                  });
                }} key={s.id}><i className="jp-resize-handle start" title="Beginn ändern" onMouseDown={e => beginSegmentDrag(e, s, 'start')} onTouchStart={e => beginSegmentDrag(e, s, 'start')} /><b>{STATUS[s.status]?.[0]}</b><span>{timeLabel(shown.start_time)}–{timeLabel(shown.end_time)}</span><small>{s.resource_type ? (RT[s.resource_type] || '') + ' ' + (data.resources.find(r => r.type === s.resource_type && Number(r.id) === Number(s.resource_id))?.name || '') : ''}</small><i className="jp-resize-handle end" title="Ende ändern" onMouseDown={e => beginSegmentDrag(e, s, 'end')} onTouchStart={e => beginSegmentDrag(e, s, 'end')} /></button>;
              })}
  </div></div>)}{visibleRows.length > 0 && <div style={{
            height: Math.max(0, totalHeight - visibleRows[visibleRows.length - 1].offset - visibleRows[visibleRows.length - 1].height)
          }} />}<div className="jp-fill" aria-hidden="true"><div className="jp-fill-label"/><div className="jp-fill-track">{ticks.map(t => <i className="jp-gridline" style={{left:x(t)+'%'}} key={t}/>)}</div></div></div></div></div>
 <ContextMenu menu={menu} onAction={menuAction} />{editor && <SegmentEditor key={editor.id ? editor.id + ':' + (editor.version || 0) : ['new',editor.job_id,editor.load_group_id,editor.start_time,editor.end_time,editor.resource_type,editor.resource_id,editor.driver_id,editor.vehicle_id].join(':')} value={editor} duplicate={value => {
      const start = unixValue(value.end_time),
        duration = unixValue(value.end_time) - unixValue(value.start_time);
      setEditor({
        ...value,
        id: undefined,
        version: undefined,
        start_time: localValue(start),
        end_time: localValue(start + duration)
      });
    }} data={data} close={() => setEditor(null)} saved={saved} />}
 {jobEditor && <JobEditor key={jobEditor.id + ':' + (jobEditor.version || 0)} job={jobEditor} close={() => setJobEditor(null)} saved={saved} remove={deleteJob} />}
 {groupEditor && <GroupEditor key={groupEditor.id + ':' + groupEditor.version} group={groupEditor} close={() => setGroupEditor(null)} saved={saved} remove={deleteGroup} />}
 {onboarding && <Onboarding initialSource={typeof onboarding === 'string' ? onboarding : 'MANUAL'} data={data} close={() => setOnboarding(false)} saved={created} />}
 {undo && <div className="jp-undo"><span>Änderung gespeichert</span><button onClick={async () => {
        try {
          await request('/planning/undo/' + undo, {
            method: 'POST'
          });
          setUndo(null);
          load();
        } catch (err) {
          showAppAlert(err.message);
        }
      }}>Rückgängig</button><button onClick={() => setUndo(null)}>×</button></div>}
 </div>;
}
